import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import './HeroSection.css';



const HeroSection = () => {
  const svgRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.hero__eyebrow', { y: 20, opacity: 0, duration: 0.7, ease: 'power3.out', delay: 0.15 });
      gsap.from('.hero__headline', { y: 40, opacity: 0, duration: 0.9, ease: 'power3.out', delay: 0.3 });
      gsap.from('.hero__sub',      { y: 30, opacity: 0, duration: 0.8, ease: 'power3.out', delay: 0.5 });
      gsap.from('.hero__desc',     { y: 25, opacity: 0, duration: 0.8, ease: 'power3.out', delay: 0.65 });
      gsap.from('.hero__btns',     { y: 20, opacity: 0, duration: 0.7, ease: 'power3.out', delay: 0.8 });
      gsap.from('.hero__trust',    { y: 15, opacity: 0, duration: 0.7, ease: 'power3.out', delay: 0.95 });

      /* SVG animations */
      gsap.to('.hero-sun', { y: -10, duration: 3.5, repeat: -1, yoyo: true, ease: 'sine.inOut' });
      gsap.to('.hero-sun-glow', { scale: 1.15, opacity: 0.6, duration: 2.5, repeat: -1, yoyo: true,
        ease: 'sine.inOut', transformOrigin: 'center center' });
      gsap.to('.hero-beam-glow', { opacity: 0.25, duration: 2.8, repeat: -1, yoyo: true, ease: 'sine.inOut' });
      gsap.to('.hero-figure',    { rotation: 1.2, transformOrigin: 'bottom center',
        duration: 4.5, repeat: -1, yoyo: true, ease: 'sine.inOut' });

      /* Staggered particle twinkle */
      gsap.to('.hero-star', {
        opacity: 0.1, scale: 0.6, stagger: { each: 0.4, repeat: -1, yoyo: true },
        duration: 1.5, ease: 'sine.inOut', transformOrigin: 'center center',
      });

      /* Ray rotation pulse */
      gsap.to('.hero-rays', { rotation: 8, duration: 8, repeat: -1, yoyo: true,
        ease: 'sine.inOut', transformOrigin: '130px 105px' });
    });
    return () => ctx.revert();
  }, []);

  return (
    <section className="hero hero-wrapper" id="home" aria-label="VTD Astrology hero">
      <div className="hero__visual" ref={svgRef}>
        <HeroIllustration />
      </div>

      <div className="hero__content">
        <p className="hero__eyebrow">✦ Vedic Astrology &bull; Jyotish &bull; Ancient Wisdom</p>

        <h1 className="hero__headline">
          Decode Your Past.<br />
          <span className="hero__headline-gold">Shape Your Future.</span>
        </h1>

        <p className="hero__sub">
          VTD certified experts decode your life journey through Vedic astrology —
          precise predictions, personal remedies, a clear path forward.
        </p>

        <p className="hero__desc">
          Authentic readings in Telugu, Hindi &amp; English. Every session is fixed price —
          no per-minute billing, no surprises.
        </p>

        <div className="hero__btns">
          <a href="/chat" className="hero__btn hero__btn--primary">
            Get Your Reading — Chat Now
          </a>
          <a href="#daily-horoscope" className="hero__btn hero__btn--secondary">
            Free Daily Horoscope
          </a>
        </div>

        <p className="hero__trust">
          <span>✦ No per-minute billing</span>
          <span>✦ VTD certified only</span>
          <span>✦ Fixed price sessions</span>
        </p>

        <p className="hero__cred">
          NIT Kurukshetra &nbsp;·&nbsp; Family lineage 3+ generations
          &nbsp;·&nbsp; 1000+ predictions &nbsp;·&nbsp; Telugu · Hindi · English
        </p>
      </div>
    </section>
  );
};

const HeroIllustration = () => (
  <svg className="hero__svg" viewBox="0 0 520 640" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="h-beamGrad" x1="0" y1="0" x2="0.2" y2="1">
        <stop offset="0%"   stopColor="#D4AF37" stopOpacity="0.85" />
        <stop offset="60%"  stopColor="#FFD700" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
      </linearGradient>
      <radialGradient id="h-personGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%"   stopColor="#FFD700" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="h-sunGrad" cx="50%" cy="50%" r="50%">
        <stop offset="0%"   stopColor="#FFD700" />
        <stop offset="100%" stopColor="#D4AF37" />
      </radialGradient>
      <radialGradient id="h-sunGlowGrad" cx="50%" cy="50%" r="50%">
        <stop offset="0%"   stopColor="#FFD700" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
      </radialGradient>
      <filter id="h-sunFilter" x="-70%" y="-70%" width="240%" height="240%">
        <feGaussianBlur stdDeviation="7" result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
      <filter id="h-figureGlow" x="-50%" y="-40%" width="200%" height="180%">
        <feGaussianBlur stdDeviation="9" result="b" />
        <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>

    {/* Ambient background glow */}
    <ellipse cx="200" cy="200" rx="180" ry="180" fill="#D4AF37" opacity="0.025" />

    {/* Stars */}
    {[[60,80,1.8],[420,50,1],[480,200,1.4],[30,320,1],[500,400,1.6],[80,520,1.2],[440,560,1],
      [360,90,1.3],[150,460,0.9],[480,310,1.1],[200,580,1],[55,180,1.5]].map(([x,y,r],i)=>(
      <circle key={i} className="hero-star" cx={x} cy={y} r={r}
        fill="#FFD700" opacity={0.35 + i * 0.04} />
    ))}

    {/* Sun halo glow */}
    <circle className="hero-sun-glow" cx="130" cy="105" r="70" fill="url(#h-sunGlowGrad)" />

    {/* Sun group */}
    <g className="hero-sun" filter="url(#h-sunFilter)">
      <circle cx="130" cy="105" r="38" fill="url(#h-sunGrad)" />
      <circle cx="130" cy="105" r="22" fill="#FFD700" />
      <g className="hero-rays">
        {[0,45,90,135,180,225,270,315].map((deg,i)=>{
          const r = (deg * Math.PI) / 180;
          return <line key={i}
            x1={130 + Math.cos(r) * 46} y1={105 + Math.sin(r) * 46}
            x2={130 + Math.cos(r) * 64} y2={105 + Math.sin(r) * 64}
            stroke="#FFD700" strokeWidth="2.8" strokeLinecap="round" />;
        })}
        {/* Secondary shorter rays */}
        {[22.5,67.5,112.5,157.5,202.5,247.5,292.5,337.5].map((deg,i)=>{
          const r = (deg * Math.PI) / 180;
          return <line key={`s${i}`}
            x1={130 + Math.cos(r) * 46} y1={105 + Math.sin(r) * 46}
            x2={130 + Math.cos(r) * 56} y2={105 + Math.sin(r) * 56}
            stroke="#FFD700" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />;
        })}
      </g>
    </g>

    {/* Beam glow pulse */}
    <ellipse className="hero-beam-glow"
      cx="260" cy="400" rx="65" ry="240" fill="#D4AF37" opacity="0.05" />

    {/* Main light beam */}
    <polygon points="106,148 156,148 345,640 178,640" fill="url(#h-beamGrad)" />

    {/* Subtle inner beam shimmer */}
    <polygon points="118,148 145,148 280,640 230,640" fill="#FFD700" opacity="0.04" />

    {/* Road path */}
    <path d="M120 640 Q220 600 285 545 Q360 465 465 380"
      stroke="#D4AF37" strokeWidth="1.5" fill="none" opacity="0.25" strokeDasharray="14 8" />

    {/* Horizon line */}
    <line x1="40" y1="580" x2="480" y2="520" stroke="#D4AF37" strokeWidth="0.8" opacity="0.15" />

    {/* Person silhouette */}
    <g className="hero-figure" filter="url(#h-figureGlow)">
      <circle cx="255" cy="438" r="21" fill="#D4AF37" opacity="0.9" />
      <path d="M242 458 L240 520 L272 520 L270 458 Z" fill="#D4AF37" opacity="0.9" />
      <line x1="242" y1="473" x2="218" y2="505"
        stroke="#D4AF37" strokeWidth="7" strokeLinecap="round" opacity="0.9" />
      <line x1="270" y1="473" x2="294" y2="505"
        stroke="#D4AF37" strokeWidth="7" strokeLinecap="round" opacity="0.9" />
      <line x1="248" y1="520" x2="240" y2="582"
        stroke="#D4AF37" strokeWidth="7.5" strokeLinecap="round" opacity="0.9" />
      <line x1="264" y1="520" x2="272" y2="582"
        stroke="#D4AF37" strokeWidth="7.5" strokeLinecap="round" opacity="0.9" />
      <ellipse cx="255" cy="510" rx="55" ry="75" fill="url(#h-personGlow)" />
    </g>

    {/* Floating orbs (cosmic effect) */}
    {[[380,220,5,0.2],[420,350,3.5,0.15],[350,480,4,0.18],[460,500,3,0.12]].map(([x,y,r,o],i)=>(
      <circle key={`orb${i}`} cx={x} cy={y} r={r} fill="#D4AF37" opacity={o} />
    ))}
  </svg>
);

export default HeroSection;
